import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { X, Plus, BookOpen, Layers } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { SkullRating } from "@/components/SkullRating";
import ReaderModal from "@/components/ReaderModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductModal({ product, onClose }) {
  const { add, setOpen } = useCart();
  const { user } = useAuth();
  const [reviews, setReviews] = useState(null);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [reading, setReading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/products/${product.id}/reviews`, { withCredentials: true });
      setReviews(r.data);
      if (r.data.my_review) {
        setRating(r.data.my_review.rating);
        setText(r.data.my_review.text);
      }
    } catch {
      setReviews({ average: null, count: 0, reviews: [], can_review: false });
    }
  };

  useEffect(() => {
    load();
    const onKey = (e) => e.key === "Escape" && !reading && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, reading]);

  const submitReview = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/products/${product.id}/reviews`, { rating, text }, { withCredentials: true });
      toast.success("Your verdict is carved in stone.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't carve that review.");
    } finally {
      setSending(false);
    }
  };

  const soldOut = product.stock === 0 && product.category !== "digital";

  return (
    <motion.div
      data-testid="product-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-void/90 backdrop-blur-md overflow-y-auto p-4 sm:p-8"
      onClick={onClose}
    >
      <div
        className="relative mx-auto max-w-4xl border-2 border-blooddeep bg-pulp my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="product-modal-close"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 bg-void/85 border border-paper/20 text-paper/70 hover:text-crimson hover:border-crimson transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        <div className="grid sm:grid-cols-2">
          <div className="relative bg-void">
            <img src={product.cover} alt={`${product.title} cover`} className="w-full h-full object-cover max-h-[70vh]" />
            {product.preorder && (
              <span data-testid="modal-preorder-badge" className="absolute top-3 -left-1 bg-yell text-black font-marker text-[11px] px-2.5 py-0.5 -rotate-3 ink-shadow-sm">
                PRE-ORDER{product.ships_by ? ` — SHIPS BY ${product.ships_by}` : ""}
              </span>
            )}
          </div>
          <div className="p-6 sm:p-8 flex flex-col">
            <p className="font-courier text-[10px] uppercase tracking-widest text-cyanx mb-2">{product.format}</p>
            <h3 className="font-impact uppercase tracking-wide text-2xl text-paper mb-2">{product.title}</h3>
            <SkullRating value={reviews?.average || 0} count={reviews?.count ?? null} testId="modal-rating" />
            <p className="font-type text-sm text-paper/60 mt-4 leading-relaxed">{product.description}</p>
            {product.bundle_titles?.length > 0 && (
              <div data-testid="modal-bundle-contents" className="mt-4 border border-blooddeep/50 bg-void/60 p-3">
                <p className="font-impact uppercase tracking-wider text-[11px] text-bloodglow mb-2 flex items-center gap-1.5">
                  <Layers size={12} /> Inside this bundle
                </p>
                <ul className="font-type text-xs text-paper/60 space-y-1">
                  {product.bundle_titles.map((t) => (
                    <li key={t}>✕ {t}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center gap-4 mt-6">
              <span className="flex items-baseline gap-2">
                {product.was_price && product.was_price > product.price && (
                  <span className="font-courier text-xs text-paper/40 line-through">${product.was_price.toFixed(2)}</span>
                )}
                <span className="font-marker text-yell text-2xl rotate-[-2deg]">${product.price.toFixed(2)}</span>
              </span>
            </div>
            <div className="flex flex-wrap gap-3 mt-4">
              <button
                data-testid="modal-add-to-cart"
                disabled={soldOut}
                onClick={() => {
                  add(product);
                  toast.success(`${product.title} rests in your coffin.`, { action: { label: "View Coffin", onClick: () => setOpen(true) } });
                }}
                className="flex items-center gap-1.5 font-impact uppercase tracking-wider text-xs bg-crimson text-white border-2 border-black px-5 py-3 ink-shadow-sm transition-all duration-150 hover:bg-crimsonhot hover:-translate-y-0.5 disabled:opacity-40"
              >
                <Plus size={14} /> {soldOut ? "Sold Out" : product.preorder ? "Pre-Order" : "Add to Coffin"}
              </button>
              {product.preview_pages?.length > 0 && (
                <button
                  data-testid="modal-read-preview"
                  onClick={() => setReading(true)}
                  className="flex items-center gap-1.5 font-impact uppercase tracking-wider text-xs text-cyanx border-2 border-cyanx/50 px-5 py-3 transition-colors duration-150 hover:bg-cyanx/10"
                >
                  <BookOpen size={14} /> Read Preview
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="border-t-2 border-blooddeep/40 p-6 sm:p-8">
          <h4 className="font-display text-lg uppercase text-paper mb-5">
            FAN <span className="text-crimson">VERDICTS</span>
          </h4>
          {reviews && reviews.reviews.length === 0 && (
            <p className="font-type text-sm text-paper/50 mb-5">No verdicts yet. The first cut is the deepest.</p>
          )}
          <div className="space-y-4 mb-6">
            {(reviews?.reviews || []).map((r) => (
              <div key={r.id || r.user_id} data-testid="review-row" className="border border-blooddeep/40 bg-void/50 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-impact uppercase tracking-wide text-xs text-paper">{r.name}</span>
                  <SkullRating value={r.rating} size={12} testId="review-row-rating" />
                </div>
                <p className="font-type text-xs text-paper/60 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
          {reviews?.can_review ? (
            <form data-testid="review-form" onSubmit={submitReview} className="border border-crimson/40 bg-void/60 p-4 space-y-3">
              <p className="font-impact uppercase tracking-wider text-xs text-bloodglow">
                {reviews.my_review ? "Update your verdict" : "You bled for this — rate it"}
              </p>
              <SkullRating value={rating} interactive onChange={setRating} size={20} testId="review-rating-input" />
              <textarea
                data-testid="review-text-input"
                rows={3}
                maxLength={500}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Spill your guts (500 characters max)..."
                className="w-full font-courier text-sm bg-void border-2 border-input text-paper px-3 py-2.5 focus:outline-none focus:border-crimson placeholder:text-paper/30 resize-none"
                required
              />
              <button
                data-testid="review-submit-button"
                disabled={sending}
                className="font-impact uppercase tracking-wider text-xs bg-blooddeep text-white border-2 border-black px-5 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson disabled:opacity-50"
              >
                {sending ? "Carving..." : "Carve Verdict"}
              </button>
            </form>
          ) : (
            <p className="font-courier text-[11px] text-paper/40 uppercase tracking-widest">
              {user ? "Buy it first — then tell us how deep it cut." : "Log in and buy it to leave a verdict."}
            </p>
          )}
        </div>
      </div>
      {reading && <ReaderModal pages={product.preview_pages} title={product.title} onClose={() => setReading(false)} />}
    </motion.div>
  );
}
